"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import DocsPageFrame from "../../../components/DocsPageFrame";
import { ArrowRight, Link2, ShieldCheck } from "lucide-react";
import Link from "next/link";

const GST = [
  {
    label: "E-Invoice (IRN)",
    desc: "Generate and cancel from an Invoice — GST portal credentials required",
  },
  {
    label: "E-Way Bill",
    desc: "Generate and cancel from an Invoice",
  },
  {
    label: "IGST / CGST / SGST",
    desc: "Split from customer state vs supplier state",
  },
  {
    label: "HSN / SAC",
    desc: "On the Item master, applied on documents",
  },
];

const CRM = [
  {
    label: "Customer master",
    desc: "Same party record in CRM and Books",
  },
  {
    label: "Item master",
    desc: "Same items, HSN, and rates",
  },
  {
    label: "Quotes into Books",
    desc: "An accepted CRM quotation can become a Books sales document — no re-keying the lines",
  },
  {
    label: "One account",
    desc: "accounts.abss.ai — Books and CRM as separate apps on the same login",
  },
];

const HOW = [
  {
    title: "1. Customer master",
    body: "There is one party record, not a copy in each app. Create the customer in CRM or in Books (Sales → Customer). If both products are on the plan, the list shows Created From as BOOKS or CRM. GSTIN, addresses, and the display name are the same record — do not add the same party twice. A party can also be a Vendor (Purchase → Vendors) without a second master.",
  },
  {
    title: "2. Item master",
    body: "There is one item. HSN / SAC and rates on All Master → Item are what Quotes, Sales Orders, and Invoices use in both apps. If both products are on the plan, Item shows Created From. CRM’s Add Item screen recommends creating the item in Books when you need inventory and accounts on it.",
  },
  {
    title: "3. Quotes into Books",
    body: "In CRM: Lead → Quotation → Convert to Sales Order. That action copies the customer and lines onto a Sales Order — you do not re-type the items. The quotation and sales order then appear in Books (Sales → Quotes / Sales Orders) with Created From = CRM. From a Books quote you can Convert To Sales Order or Convert To Invoice. A quote does not post to the ledger; Accounts Receivable and Sales post when the Invoice is saved (and Payment Received when it is approved).",
  },
  {
    title: "4. One account",
    body: "One login at accounts.abss.ai. Books and CRM are separate apps (separate trials and plans). After sign-in, the avatar drawer My Apps opens the other product; My account returns to the accounts portal. Roles and permissions are per app — a Books role does not grant CRM screens, and the reverse.",
  },
];

const sections = [
  { heading: "What it is" },
  { heading: "GST on documents" },
  { heading: "With ABSS CRM" },
];

export default function AboutBooksPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.1 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[32px] sm:text-[40px] md:text-[46px] leading-[1.15] text-[#171717] sentient-medium mb-5">
            About ABSS Books
          </h1>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#525252] geist-regular max-w-2xl">
            Double-entry books plus stock, GST, and production — not a CRM.
            Module map is on Welcome.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[22px] sm:text-[26px] sentient-medium text-[#171717] mb-3"
          >
            What it is
          </h2>
          <p className="text-[15px] leading-[1.8] text-[#525252] geist-regular max-w-2xl">
            Every Invoice, Bill, Payment Received, Payment Made, and Manual
            Journal posts to Chart of Accounts / Account Ledger as it is
            approved. Roles under Settings control Create / View / Edit /
            Delete / Export per module — there is no fixed Admin / Manager /
            Sales map.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[22px] sm:text-[26px] sentient-medium text-[#171717] mb-4"
          >
            GST on documents
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {GST.map((item) => (
              <div
                key={item.label}
                className="border border-[#E5E5E5] rounded-[12px] p-4 bg-white flex items-start gap-3"
              >
                <ShieldCheck className="w-4 h-4 text-[#1785B6] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[14px] geist-medium text-[#171717] mb-0.5">
                    {item.label}
                  </p>
                  <p className="text-[12px] geist-regular text-[#525252] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="mb-12 p-5 border border-[#DBEAFE] bg-[#EFF8FF] rounded-[14px]"
        >
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 text-[#1E40AF]" />
            <h2
              id="section-2"
              className="text-[18px] sentient-medium text-[#1E40AF]"
            >
              With ABSS CRM
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {CRM.map((item) => (
              <div
                key={item.label}
                className="border border-[#DBEAFE] rounded-[12px] p-4 bg-white flex items-start gap-3"
              >
                <ArrowRight className="w-4 h-4 text-[#1785B6] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[14px] geist-medium text-[#171717] mb-0.5">
                    {item.label}
                  </p>
                  <p className="text-[12px] geist-regular text-[#525252] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-[13px] geist-medium text-[#1E40AF] mb-3 px-4 py-3 bg-white border border-[#DBEAFE] rounded-[10px]">
            CRM: Lead{" "}
            <span className="text-[#93C5FD]">→</span> Quotation{" "}
            <span className="text-[#93C5FD]">→</span> Convert to Sales Order{" "}
            <span className="text-[#93C5FD]">→</span> Books: Delivery Challan /
            Invoice{" "}
            <span className="text-[#93C5FD]">→</span> Payment Received
          </p>

          <div className="space-y-3">
            {HOW.map((item) => (
              <div
                key={item.title}
                className="border border-[#DBEAFE] rounded-[12px] p-4 bg-white"
              >
                <p className="text-[14px] geist-medium text-[#171717] mb-1">
                  {item.title}
                </p>
                <p className="text-[13px] geist-regular text-[#374151] leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
          <p className="text-[13px] geist-regular text-[#1E40AF] leading-relaxed mt-4">
            If the company used CRM first, Home → Getting Started can show Data
            Setup Required (customer ledger, branches, item inventory,
            warehouses, quote / sales-order locations). That is a one-time
            admin backfill, not a second master.
          </p>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/getting-started/welcome"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              Welcome <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Left-menu modules
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              IRN and e-way on the invoice
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
